export interface CookieConsentData {
  necessary: boolean;
  analytics: boolean;
  marketing: boolean;
  preferences: boolean;
  timestamp: number;
  version: string;
}

export type CookieCategory = 'necessary' | 'analytics' | 'marketing' | 'preferences';

export class CookieConsentManager {
  private static instance: CookieConsentManager;
  private readonly CONSENT_KEY = 'cookie-consent';
  private readonly CONSENT_VERSION = '1.0';
  private readonly CONSENT_EXPIRY_DAYS = 365;
  
  private listeners: Array<(consent: CookieConsentData) => void> = [];

  private constructor() {}

  static getInstance(): CookieConsentManager {
    if (!CookieConsentManager.instance) {
      CookieConsentManager.instance = new CookieConsentManager();
    }
    return CookieConsentManager.instance;
  }

  // Get current consent status
  getConsent(): CookieConsentData | null {
    if (typeof window === 'undefined') return null;
    
    try {
      const stored = localStorage.getItem(this.CONSENT_KEY);
      if (!stored) return null;
      
      const consent = JSON.parse(stored) as CookieConsentData;
      
      // Check if consent is expired (1 year)
      const expiry = consent.timestamp + (this.CONSENT_EXPIRY_DAYS * 24 * 60 * 60 * 1000);
      if (Date.now() > expiry) {
        this.clearConsent();
        return null;
      }
      
      // Check if version matches
      if (consent.version !== this.CONSENT_VERSION) {
        this.clearConsent();
        return null;
      }
      
      return consent;
    } catch (error) {
      console.error('Error reading cookie consent:', error);
      return null;
    }
  }

  // Save consent preferences
  saveConsent(consent: Omit<CookieConsentData, 'timestamp' | 'version'>): void {
    if (typeof window === 'undefined') return;
    
    const consentData: CookieConsentData = {
      ...consent,
      timestamp: Date.now(),
      version: this.CONSENT_VERSION
    };
    
    try {
      localStorage.setItem(this.CONSENT_KEY, JSON.stringify(consentData));
      this.notifyListeners(consentData);
      this.updateCookies(consentData);
    } catch (error) {
      console.error('Error saving cookie consent:', error);
    }
  }

  // Accept all cookies
  acceptAll(): void {
    this.saveConsent({
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true
    });
  }

  // Accept only necessary cookies
  acceptNecessary(): void {
    this.saveConsent({
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false
    });
  }

  // Clear consent data
  clearConsent(): void {
    if (typeof window === 'undefined') return;
    localStorage.removeItem(this.CONSENT_KEY);
    this.clearNonNecessaryCookies();
  }

  // Check if user has given consent
  hasConsent(): boolean {
    return this.getConsent() !== null;
  }

  // Check if specific category is allowed
  isCategoryAllowed(category: CookieCategory): boolean {
    const consent = this.getConsent();
    return consent ? consent[category] : false;
  }

  // Add listener for consent changes
  addListener(listener: (consent: CookieConsentData) => void): void {
    this.listeners.push(listener);
  }

  // Remove listener
  removeListener(listener: (consent: CookieConsentData) => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  // Notify all listeners
  private notifyListeners(consent: CookieConsentData): void {
    this.listeners.forEach(listener => {
      try {
        listener(consent);
      } catch (error) {
        console.error('Error in consent listener:', error);
      }
    });
  }

  // Update cookies based on consent
  private updateCookies(consent: CookieConsentData): void {
    if (!consent.analytics) {
      this.deleteCookiesByCategory('analytics');
    }
    
    if (!consent.marketing) {
      this.deleteCookiesByCategory('marketing');
    }
    
    if (!consent.preferences) {
      this.deleteCookiesByCategory('preferences');
    }
  }

  // Clear non-necessary cookies
  private clearNonNecessaryCookies(): void {
    this.deleteCookiesByCategory('analytics');
    this.deleteCookiesByCategory('marketing');
    this.deleteCookiesByCategory('preferences');
  }

  // Delete cookies by category
  private deleteCookiesByCategory(category: string): void {
    if (typeof document === 'undefined') return;
    
    const cookiesToDelete: Record<string, string[]> = {
      analytics: ['_ga', '_ga_', '_gid', '_gat', '_gtag'],
      marketing: ['_fbp', '_fbc', 'fr'],
      preferences: ['theme', 'language']
    };
    
    const cookies = cookiesToDelete[category] || [];
    
    cookies.forEach(cookieName => {
      // Delete from current domain
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
      document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname};`;
      
      // For Google Analytics cookies with dynamic suffixes
      if (cookieName.endsWith('_')) {
        const allCookies = document.cookie.split(';');
        allCookies.forEach(cookie => {
          const name = cookie.split('=')[0].trim();
          if (name.startsWith(cookieName)) {
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;`;
            document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname};`;
          }
        });
      }
    });
  }

  // Get cookie descriptions for UI
  getCookieDescriptions() {
    return {
      necessary: {
        title: 'Cookies nécessaires',
        description: 'Ces cookies sont essentiels au fonctionnement du site web et ne peuvent pas être désactivés.',
        examples: ['Préférences de session', 'Sécurité', 'Fonctionnalités de base']
      },
      analytics: {
        title: 'Cookies d\'analyse',
        description: 'Ces cookies nous aident à comprendre comment les visiteurs interagissent avec le site web.',
        examples: ['Google Analytics', 'Statistiques de visite', 'Performance']
      },
      marketing: {
        title: 'Cookies marketing',
        description: 'Ces cookies sont utilisés pour personnaliser les publicités et mesurer leur efficacité.',
        examples: ['Facebook Pixel', 'Publicités ciblées', 'Remarketing']
      },
      preferences: {
        title: 'Cookies de préférences',
        description: 'Ces cookies nous permettent de mémoriser vos préférences et de personnaliser votre expérience.',
        examples: ['Thème sombre/clair', 'Langue', 'Préférences d\'affichage']
      }
    };
  }
}

// Export singleton instance
export const cookieConsent = CookieConsentManager.getInstance();
